import React, { useState, useEffect, lazy, Suspense } from 'react'
import Link from 'components/Link'
import { CallToAction } from 'components/CallToAction'

import { useApp } from '../../context/App'
import { useLocation } from '@reach/router'
import { Dialog as RadixDialog } from 'radix-ui'
import { IconX, IconHome, IconCreditCard, IconApps, IconStack, IconInfo, IconHeadset } from '@posthog/icons'

const TalkToAHuman = lazy(() => import('../../pages/talk-to-a-human'))

const navLinks = [
    { name: 'Home', url: '/', icon: IconHome },
    { name: 'Pricing', url: '/pricing', icon: IconCreditCard },
    { name: 'Services', url: '/services', icon: IconApps },
    { name: 'Stack', url: '/stack', icon: IconStack },
    { name: 'About', url: '/about', icon: IconInfo },
]

export default function SiteLayout({ children }: { children: React.ReactNode }) {
    const { showContact, setShowContact } = useApp()
    const location = useLocation()

    useEffect(() => {
        const handleHashChange = () => {
            if (window.location.hash === '#contact') {
                setShowContact(true)
            }
        }
        handleHashChange()
        window.addEventListener('hashchange', handleHashChange)
        return () => window.removeEventListener('hashchange', handleHashChange)
    }, [])

    return (
        <div
            data-scheme="primary"
            className="max-w-7xl mx-auto border-x border-primary bg-primary shadow-xl min-h-screen flex flex-col"
            id="app-container"
        >
            {/* Top nav */}
            <div
                id="taskbar"
                data-scheme="primary"
                data-menu-container
                className="w-full bg-accent/75 backdrop-blur border-b border-primary sticky top-0 z-40 pl-0.5 pr-2 py-1"
            >
                <div className="mx-auto transition-all duration-300 flex justify-between items-center w-full max-w-7xl">
                    <div className="flex gap-px items-center h-full">
                        <Link
                            to="/"
                            title="Marno - Home"
                            className="flex items-center no-underline px-1.5 py-0.5 text-[13px] leading-none text-primary"
                        >
                            <img src="/images/logo-icon-one.svg" alt="Marno" title="Marno" className="h-[40px]" />
                        </Link>
                        {navLinks.map((link) => (
                            <Link
                                key={link.name}
                                to={link.url}
                                title={link.name}
                                className="group flex select-none items-center justify-between gap-0.5 rounded px-1.5 py-0.5 text-[13px] leading-none text-black dark:text-white font-medium hover:text-primary outline-none hover:bg-accent-2 no-underline hidden md:flex"
                            >
                                {link.name}
                            </Link>
                        ))}
                        <Link
                            to="#contact"
                            onClick={() => setShowContact(true)}
                            title="Contact"
                            className="group flex select-none items-center justify-between gap-0.5 rounded px-1.5 py-0.5 text-[13px] leading-none text-black dark:text-white font-medium hover:text-primary outline-none hover:bg-accent-2 no-underline hidden md:flex"
                        >
                            Contact
                        </Link>
                    </div>
                    <aside data-scheme="secondary" className="flex items-center gap-0.5">
                        <div className="relative mr-1 scale-90 origin-center">
                            <CallToAction onClick={() => setShowContact(true)} size="sm" title="Get Started">
                                Get Started
                            </CallToAction>
                        </div>
                    </aside>
                </div>
            </div>

            {/* Content */}
            <main className="flex-1 pb-16 md:pb-0">{children}</main>

            {/* Footer - desktop only */}
            <footer className="@container z-40 hidden md:block">
                <div data-scheme="secondary" className="bg-primary border-t border-primary">
                    <div className="p-3 @lg:px-8 bg-accent/30 border-t border-primary/10 text-xs">
                        <div className="text-right">
                            <p className="text-secondary m-0">&copy; {new Date().getFullYear()} Marno, Inc.</p>
                        </div>
                    </div>
                </div>
            </footer>

            {/* Mobile bottom tab bar */}
            <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-accent border-t border-primary z-[9999]">
                <div className="grid grid-cols-6 gap-[2px]">
                    {navLinks.map((link) => {
                        const isActive = location.pathname === link.url
                        const Icon = link.icon
                        return (
                            <Link
                                key={link.name}
                                to={link.url}
                                title={link.name}
                                className={`flex flex-col items-center justify-center py-2 text-[10px] no-underline ${
                                    isActive
                                        ? 'bg-primary font-bold text-primary'
                                        : 'text-black opacity-70 hover:opacity-100'
                                }`}
                            >
                                <Icon className="w-5 h-5 mb-0.5" />
                                {link.name}
                            </Link>
                        )
                    })}
                    <button
                        onClick={() => setShowContact(true)}
                        title="Contact"
                        className={`flex flex-col items-center justify-center py-2 text-[10px] border-none bg-transparent cursor-pointer ${
                            showContact
                                ? 'bg-primary font-bold text-primary'
                                : 'text-black opacity-70 hover:opacity-100'
                        }`}
                    >
                        <IconHeadset className="w-5 h-5 mb-0.5" />
                        Contact
                    </button>
                </div>
            </nav>

            {/* Contact Popup - native RadixUI Dialog */}
            <RadixDialog.Root open={showContact} onOpenChange={setShowContact}>
                <RadixDialog.Portal>
                    <RadixDialog.Overlay className="bg-black/50 fixed inset-0 z-50" />
                    <RadixDialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(48rem,calc(100%-2rem))] max-h-[85vh] bg-primary text-primary z-50 overflow-auto">
                        <div data-scheme="primary">
                            <div className="bg-accent flex items-center justify-between px-3 py-1.5 border-b border-primary">
                                <p className="text-primary text-left text-base font-semibold ml-1 my-0">
                                    Talk to a human
                                </p>
                                <RadixDialog.Close asChild>
                                    <button className="flex items-center justify-center rounded px-2 py-1 text-primary hover:bg-accent-2 cursor-pointer border-none bg-transparent">
                                        <IconX className="size-4" />
                                    </button>
                                </RadixDialog.Close>
                            </div>
                            <Suspense fallback={<div className="p-8 text-center">Loading...</div>}>
                                <TalkToAHuman />
                            </Suspense>
                        </div>
                    </RadixDialog.Content>
                </RadixDialog.Portal>
            </RadixDialog.Root>
        </div>
    )
}
